import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import { NotesSyncApiService } from '../notes-sync-api.service';
import { LogService } from '../log.service';
import { SyncStateService } from './sync-state.service';

export interface RemoteChangesResult {
  results: any[];
  last_seq: string;
}
export interface EnrichedChange {
  id: string;
  deleted?: boolean;
  doc?: any;
}

/**
 * Every Space is a separate database server-side (see tasks/space_core_client.md, Stage 2 §4), so
 * `/api/notes/changes` and `/api/notes/bulk_get` are per-Space calls with their own independent
 * change-feed cursor. This service tracks that cursor (and the last remote seq observed) per
 * Space, lazily, rather than assuming a single global one.
 */
@Injectable({ providedIn: 'root' })
export class RemoteChangesService {
  /** Last sequence successfully acknowledged by the client, per Space. */
  private readonly lastPulledSeqSubjects = new Map<string, BehaviorSubject<string>>();

  /** Last sequence observed on the server, per Space. */
  private readonly remoteSeqSubjects = new Map<string, BehaviorSubject<string>>();

  constructor(
    private readonly api: NotesSyncApiService,
    private readonly logs: LogService,
    private readonly syncState: SyncStateService
  ) {}

  private lastPulledSeqSubject(spaceId: string): BehaviorSubject<string> {
    let subject = this.lastPulledSeqSubjects.get(spaceId);
    if (!subject) {
      subject = new BehaviorSubject<string>('0');
      this.lastPulledSeqSubjects.set(spaceId, subject);
    }
    return subject;
  }

  private remoteSeqSubject(spaceId: string): BehaviorSubject<string> {
    let subject = this.remoteSeqSubjects.get(spaceId);
    if (!subject) {
      subject = new BehaviorSubject<string>('0');
      this.remoteSeqSubjects.set(spaceId, subject);
    }
    return subject;
  }

  getLastPulledSeq(spaceId: string): string {
    return this.lastPulledSeqSubject(spaceId).value;
  }

  /**
   * (Re-)seeds a Space's in-memory cursor from persisted state. Always re-reads — cheap
   * (localStorage), and safe/necessary to call unconditionally at the start of every per-Space
   * pull, since it's also what makes an in-session account switch correct: a Space that both the
   * old and new account belong to (a SHARED Space) must never keep showing the previous account's
   * cursor (see SyncCoordinatorService.onAccountChanged()/forgetAll() below).
   *
   * `isPrivateSpace` should be true only for the caller's own PRIVATE Space — see
   * SyncStateService.getLastSeq() for what that enables (one-time legacy-cursor migration).
   */
  loadPersistedSeq(spaceId: string, isPrivateSpace = false): void {
    const stored = this.syncState.getLastSeq(spaceId, isPrivateSpace);
    this.lastPulledSeqSubject(spaceId).next(stored);
  }

  /**
   * One-shot check for remote changes in one Space.
   * NO decisions here – coordinator decides what to do.
   *
   * `spaceId` is the local cursor key (always a real, defined identifier — see the Map-based
   * bookkeeping above). `remoteSpaceId` is what actually goes on the wire, and may be `undefined`:
   * the coordinator's getSpaces()-failure fallback (tasks/space_core_client.md Stage 2; see
   * SyncCoordinatorService.LEGACY_SPACE_ID) tracks a cursor under a local-only sentinel key while
   * still issuing the pre-multi-Space, no-spaceId-filter request shape.
   *
   * Deliberately NOT a default parameter (`= spaceId`): a default only applies when the argument is
   * *omitted*, but also fires on an explicitly-passed `undefined` — which is exactly the value the
   * fallback case needs to pass through untouched. A default here would silently turn that
   * `undefined` back into `spaceId` (the sentinel), and re-leak it onto the wire.
   */
  async checkOnce(spaceId: string, since: string, remoteSpaceId: string | undefined): Promise<RemoteChangesResult> {
    const { results, last_seq } = await this.api.getChanges(since, remoteSpaceId);

    if (!results.length) {
      this.remoteSeqSubject(spaceId).next(last_seq);
      return { results: [], last_seq };
    }

    // The changes feed carries each live note's current content in `doc` (same shape as bulk_get), so
    // usually there is nothing more to fetch. Only a live row WITHOUT a doc (rare — the contract
    // doesn't guarantee one) falls back to bulk_get; deleted rows (incl. moves to Trash) need none.
    const hasDoc = (c: any) => !!c?.doc && typeof c.doc === 'object' && !!c.doc._id;
    const toFetch = results.filter((c: any) => !c.deleted && !hasDoc(c));

    const fetched = new Map<string, any>();
    const trashed = new Set<string>();
    if (toFetch.length) {
      this.logs.append('synch', `[PULL] ${toFetch.length} change row(s) without content — fetching via bulk_get`);
      const { docs, errors } = await this.api.bulkGetNotes(
        toFetch.map((c: any) => ({ id: c.id, rev: c.changes?.[0]?.rev })),
        remoteSpaceId,
      );
      for (const d of docs) if (d?._id) fetched.set(d._id, d);
      for (const e of errors) {
        // Moved to Trash after the feed was read: the same thing as a delete for this device.
        if (e?.error === 'trashed' && e.note_id) trashed.add(e.note_id);
      }
    }

    const enriched: EnrichedChange[] = results.map((c: any) => {
      const deleted = !!c.deleted || trashed.has(c.id);
      return {
        id: c.id,
        deleted,
        doc: deleted ? undefined : hasDoc(c) ? c.doc : fetched.get(c.id),
      };
    });

    this.remoteSeqSubject(spaceId).next(last_seq);

    return {
      results: enriched,
      last_seq
    };
  }

  /**
   * Acknowledge that pull has completed successfully for one Space.
   * (Called by the pull executor / coordinator.)
   */
  markPulled(spaceId: string, lastSeq: string): void {
    this.lastPulledSeqSubject(spaceId).next(lastSeq);
    this.syncState.setLastSeq(spaceId, lastSeq);
  }

  resetLastSeq(spaceId: string): void {
    this.lastPulledSeqSubject(spaceId).next('0');
    this.remoteSeqSubject(spaceId).next('0');
    this.syncState.clearSpace(spaceId);
    this.logs.append('synch', `[REMOTE] last_seq reset to 0 for space ${spaceId}`);
  }

  /** Resets every currently-tracked Space's cursor, plus this account's persisted state entirely —
   * used by the manual "Reset sync cursor" debug action, whose historical meaning was "resync
   * everything from scratch", not "resync one Space". */
  resetAllLastSeq(): void {
    for (const spaceId of this.lastPulledSeqSubjects.keys()) {
      this.lastPulledSeqSubject(spaceId).next('0');
      this.remoteSeqSubject(spaceId).next('0');
    }
    this.syncState.clear();
    this.logs.append('synch', '[REMOTE] last_seq reset to 0 for all spaces');
  }

  /** Forgets all in-memory per-Space cursor state (but NOT persisted storage) — call after an
   * in-session account switch. Each Space's pull re-seeds itself from persisted state via
   * loadPersistedSeq() at the start of its own pull anyway (which is itself account-scoped via
   * SyncStateService), so this only prevents a stale value from being visible in the moment
   * between the switch and that first re-seed. */
  forgetAll(): void {
    this.lastPulledSeqSubjects.clear();
    this.remoteSeqSubjects.clear();
  }

  /** Optional debug snapshot for one Space. */
  getDebugState(spaceId: string) {
    return {
      lastPulledSeq: this.lastPulledSeqSubject(spaceId).value,
      remoteSeq: this.remoteSeqSubject(spaceId).value
    };
  }
}
